import { useState } from 'react';
import { formatBR, type DashboardPeriod } from '@control-center/shared';

import { PageHelp } from '../components/common/PageHelp';
import { AlertsStrip } from '../components/dashboard/AlertsStrip';
import { ChargesTodayTable } from '../components/dashboard/ChargesTodayTable';
import { DivergenceWidget } from '../components/dashboard/DivergenceWidget';
import { FiscalDashboardCards } from '../components/dashboard/FiscalDashboardCards';
import { InadimplentesTable } from '../components/dashboard/InadimplentesTable';
import { KpiCard, KpiCardCount } from '../components/dashboard/KpiCard';
import { RevenueByProductChart } from '../components/dashboard/RevenueByProductChart';
import { RevenueMonthlyChart } from '../components/dashboard/RevenueMonthlyChart';
import { SetupChecklist } from '../components/setup/SetupChecklist';
import { useDashboardAlerts, useDashboardKpis } from '../hooks/use-dashboard';

const periodLabels: Record<DashboardPeriod, string> = {
  current_month: 'Mês atual',
  last_month: 'Mês anterior',
  last_3m: 'Últimos 3 meses',
  last_12m: 'Últimos 12 meses',
};

/**
 * Dashboard Operacional — Epic E6.
 *
 * Layout vertical:
 * 1. Period selector + título
 * 2. AlertsStrip (story 6.6) — só renderiza se há alertas
 * 3. KPI cards (story 6.1) — MRR, Recebido, Pendente, Vencido + 2 counts
 * 4. Widgets de detalhe — Inadimplentes top 10 (6.2), Cobranças hoje (6.3),
 *    Receita por produto (6.4), Receita 12m (6.5), Divergence widget (6.8)
 *
 * Dados via mrr_snapshots cron (story 6.7, 00:30 BRT). Sem dados → snapshot_date=null.
 */
export function DashboardPage() {
  const [period, setPeriod] = useState<DashboardPeriod>('current_month');
  const kpisQuery = useDashboardKpis(period);
  const alertsQuery = useDashboardAlerts();

  const kpis = kpisQuery.data?.data;
  const alerts = alertsQuery.data?.data.alerts ?? [];

  return (
    <div className="mx-auto flex max-w-7xl flex-col gap-6 px-6 py-6">
      {/* Header */}
      <div className="flex items-end justify-between gap-3">
        <div className="flex items-start gap-2">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight text-ink">Dashboard</h1>
            <p className="mt-1 text-sm text-muted">
              {kpis?.snapshot_date
                ? `Snapshot de ${formatBR(kpis.snapshot_date)}`
                : 'Sem snapshot disponível (cron roda 00:30 BRT diário)'}
            </p>
          </div>
          <PageHelp id="dashboard" />
        </div>
        <select
          value={period}
          onChange={(e) => setPeriod(e.target.value as DashboardPeriod)}
          className="rounded-md border border-line bg-panel-2 px-3 py-1.5 text-sm text-ink"
        >
          {Object.entries(periodLabels).map(([key, label]) => (
            <option key={key} value={key}>
              {label}
            </option>
          ))}
        </select>
      </div>

      {/* Onboarding — checklist colapsável, some quando 100% */}
      <SetupChecklist />

      {/* Story 6.6 — Alerts strip */}
      <AlertsStrip alerts={alerts} />

      {/* Story 6.1 — KPI cards */}
      {kpisQuery.isLoading && <KpisSkeleton />}
      {kpisQuery.isError && (
        <div className="rounded-lg border border-bad/40 bg-bad/10 p-4 text-sm text-ink">
          Erro ao carregar KPIs. Tente recarregar a página.
        </div>
      )}
      {kpis && (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
          <KpiCard label="MRR" card={kpis.mrr} subtitle="vs período anterior" />
          <KpiCard label="Recebido" card={kpis.revenue_received} subtitle="vs período anterior" />
          <KpiCard label="Pendente" card={kpis.revenue_pending} subtitle="vs período anterior" />
          <KpiCard label="Vencido" card={kpis.revenue_overdue} subtitle="vs período anterior" />
        </div>
      )}

      {/* Counts auxiliares */}
      {kpis && (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <KpiCardCount label="Clientes ativos" value={kpis.active_clients} />
          <KpiCardCount label="Inadimplentes" value={kpis.inadimplent_clients} />
          <KpiCardCount label="Cobranças vencidas" value={kpis.overdue_count} />
        </div>
      )}

      {/* Story 10.7 AC8 + Story 10.8 AC7 — Cards fiscais */}
      <FiscalDashboardCards />

      {/* Story 6.2 + 6.3 — Inadimplentes top 10 + Charges today */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <InadimplentesTable />
        <ChargesTodayTable />
      </div>

      {/* Story 6.4 — Receita por produto */}
      <RevenueByProductChart />

      {/* Story 6.5 — Receita mensal 12m */}
      <RevenueMonthlyChart />

      {/* Story 6.8 — Divergence widget */}
      <DivergenceWidget />
    </div>
  );
}

function KpisSkeleton() {
  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
      {[0, 1, 2, 3].map((i) => (
        <div key={i} className="h-[120px] animate-pulse rounded-lg border border-line bg-panel-2" />
      ))}
    </div>
  );
}
